/**
 * Topic completed / manage topic modal (time actions only).
 * Quiz push is handled by sequenced ConfirmPushQuizModal outside.
 */

import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, SkipForward, Timer, X } from "lucide-react";
import type { SessionTopicPlan } from "./topicProgress";
import { playTopicTimeUpBeep, stopTopicTimeUpBeep } from "./topicTimeUpBeep";

export type TopicManageAction = "completed" | "more-time" | "skip";

type ManageTopicModalProps = {
  open: boolean;
  topic?: SessionTopicPlan | null;
  /** Shown when earlyAccess; e.g. "5:30" */
  elapsedLabel?: string;
  /** Planned / reached limit label, e.g. "10m 00s" */
  timeLimitLabel?: string;
  /** True when trainer opens manually before time runs out */
  earlyAccess?: boolean;
  /** Allow closing without applying (manual manage only by default) */
  dismissible?: boolean;
  /** Current subtopic name (when topic has subtopics) */
  subtopicName?: string | null;
  /** Subtopic elapsed label */
  subtopicElapsedLabel?: string;
  /** Subtopic time limit label */
  subtopicTimeLimitLabel?: string;
  /** Topic or subtopic id — used for one-time time-up beep */
  segmentId?: string | null;
  onClose: () => void;
  onApply: (action: TopicManageAction) => void;
};

const ACTIONS: {
  id: TopicManageAction;
  label: string;
}[] = [
  { id: "completed", label: "Completed" },
  { id: "more-time", label: "Need more time (+10m)" },
  { id: "skip", label: "Skip" },
];

export function formatTimeLimitLabel(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function ActionIcon({
  id,
  active,
}: {
  id: TopicManageAction;
  active: boolean;
}) {
  const color = active ? "#2563eb" : "#64748b";
  if (id === "completed") {
    return <CheckCircle2 size={20} color={color} />;
  }
  if (id === "more-time") {
    return <Timer size={20} color={color} />;
  }
  return <SkipForward size={20} color={color} />;
}

export function ManageTopicModal({
  open,
  topic,
  elapsedLabel,
  timeLimitLabel,
  earlyAccess = false,
  dismissible,
  subtopicName,
  subtopicElapsedLabel,
  subtopicTimeLimitLabel,
  segmentId,
  onClose,
  onApply,
}: ManageTopicModalProps) {
  const [selected, setSelected] = useState<TopicManageAction>("completed");
  const canDismiss = dismissible ?? earlyAccess;
  /** Beep at most once per topic/subtopic time-up (never on manual manage / re-open). */
  const beepedSegmentIdRef = useRef<string | null>(null);
  const activeSegmentId = segmentId ?? topic?.id ?? null;

  useEffect(() => {
    if (open) setSelected("completed");
  }, [open, activeSegmentId]);

  useEffect(() => {
    if (!open || !topic) {
      stopTopicTimeUpBeep();
      return;
    }
    // Manual "Manage topic" before time is up — no sound.
    if (earlyAccess) return;
    // Once per segment time-up; reopen / prop flicker must not re-beep.
    if (!activeSegmentId || beepedSegmentIdRef.current === activeSegmentId) return;
    beepedSegmentIdRef.current = activeSegmentId;
    playTopicTimeUpBeep();
  }, [open, activeSegmentId, earlyAccess, topic]);

  if (!open || !topic) return null;

  const plannedLabel =
    subtopicName && subtopicTimeLimitLabel
      ? subtopicTimeLimitLabel
      : timeLimitLabel ?? formatTimeLimitLabel(topic.durationMin * 60);
  const elapsedForHeader =
    subtopicName && subtopicElapsedLabel ? subtopicElapsedLabel : elapsedLabel;
  const selectedLabel =
    ACTIONS.find((a) => a.id === selected)?.label ?? "Completed";

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 210,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        padding: 16,
      }}
      onClick={canDismiss ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-topic-title"
        style={{
          width: "100%",
          maxWidth: 512,
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 16,
          background: "#fff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 24px",
            background: earlyAccess ? "#eff6ff" : "#fff1f2",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: earlyAccess ? "#dbeafe" : "#ffe4e6",
              }}
            >
              <Timer
                size={20}
                color={earlyAccess ? "#2563eb" : "#e11d48"}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2
                id="manage-topic-title"
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: earlyAccess ? "#1e40af" : "#9f1239",
                }}
              >
                {earlyAccess
                  ? "Manage current topic"
                  : "Topic time completed"}
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 14,
                  fontWeight: 600,
                  color: earlyAccess ? "#2563eb" : "#e11d48",
                }}
              >
                {`Planned: ${plannedLabel}${
                  elapsedForHeader ? ` · Elapsed: ${elapsedForHeader}` : ""
                }`}
              </p>
            </div>
          </div>
          {canDismiss ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                border: "none",
                background: "transparent",
                color: "#94a3b8",
                cursor: "pointer",
                padding: 4,
                lineHeight: 0,
                flexShrink: 0,
              }}
            >
              <X size={20} />
            </button>
          ) : null}
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          <p
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {subtopicName
              ? earlyAccess
                ? `${subtopicName} — finish or adjust before time is up.`
                : `${subtopicName} planned time is over.`
              : earlyAccess
                ? `${topic.name} — finish or adjust before time is up.`
                : `${topic.name} planned time is over.`}
          </p>
          {subtopicName && (
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
              Topic: {topic.name}
              {subtopicElapsedLabel && subtopicTimeLimitLabel && (
                <span style={{ marginLeft: 8 }}>
                  · Subtopic: {subtopicElapsedLabel} / {subtopicTimeLimitLabel}
                </span>
              )}
            </p>
          )}
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 14,
              color: "#64748b",
            }}
          >
            What next?
          </p>

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 12,
            }}
          >
            {ACTIONS.map((action) => {
              const active = selected === action.id;
              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => setSelected(action.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px",
                    borderRadius: 12,
                    border: active
                      ? "2px solid #2563eb"
                      : "2px solid #e2e8f0",
                    background: active ? "#eff6ff" : "#fff",
                    color: active ? "#1d4ed8" : "#334155",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                    gridColumn:
                      action.id === "skip" ? "1 / 2" : undefined,
                  }}
                >
                  <ActionIcon id={action.id} active={active} />
                  {action.label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              // Close first so a second click cannot re-apply Completed
              onClose();
              onApply(selected);
            }}
            style={{
              marginTop: 16,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              borderRadius: 12,
              border: "none",
              background: "#1e293b",
              padding: "12px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <CheckCircle2 size={16} />
            Apply: {selectedLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
